import { useMemo } from 'react';

import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useSelector } from 'react-redux';

import { useComparisonMode } from './useComparisonMode';
import { useHumanAgentsQueryVariables } from './useHumanAgentsQueryVariables';
import type { EffectivenessMetrics } from '../components/HumanAgentsEffectiveness/effectivenessChartConfig';
import { selectAnalyticsDates } from '../redux/selectors';
import { isSingleDayRange } from '../utils/formatDateRangeLabel';
import { getComparisonDates } from '../utils/getComparisonDates';

export type HumanAgentsEffectivenessTimeseriesRow = EffectivenessMetrics & { time_bucket: string };

interface QueryResponse {
  rows: HumanAgentsEffectivenessTimeseriesRow[];
}

const EFFECTIVENESS_TIMESERIES_FIELDS = `
  time_bucket
  first_response_min
  first_response_max
  first_response_avg
  first_response_median
  response_min
  response_max
  response_avg
  response_median
  handling_min
  handling_max
  handling_avg
  handling_median
  resolution_min
  resolution_max
  resolution_avg
  resolution_median
`;

export const GET_HUMAN_AGENTS_DURATIONS_TIMESERIES = gql`
  query HumanAgentsDurationsTimeseries(
    $start_time: timestamp!
    $end_time: timestamp!
    $desk_ids: _uuid
    $channels: _text
    $channel_types: _text
    $agent_ids: _text
    $csat_human_agent: _int4
    $min_num_user_messages: Int
    $is_test: Boolean
    $user_rating: _int4
  ) {
    rows: human_agents_durations_timeseries(
      args: {
        start_time: $start_time
        end_time: $end_time
        desk_ids: $desk_ids
        channels: $channels
        channel_types: $channel_types
        agent_ids: $agent_ids
        csat_human_agent: $csat_human_agent
        min_num_user_messages: $min_num_user_messages
        is_test: $is_test
        user_rating: $user_rating
      }
    ) {
      ${EFFECTIVENESS_TIMESERIES_FIELDS}
    }
  }
`;

const GET_HUMAN_AGENTS_DURATIONS_TIMESERIES_COMPARISON = gql`
  query HumanAgentsDurationsTimeseriesComparison(
    $start_time: timestamp!
    $end_time: timestamp!
    $desk_ids: _uuid
    $channels: _text
    $channel_types: _text
    $agent_ids: _text
    $csat_human_agent: _int4
    $min_num_user_messages: Int
    $is_test: Boolean
    $user_rating: _int4
  ) {
    rows: human_agents_durations_timeseries(
      args: {
        start_time: $start_time
        end_time: $end_time
        desk_ids: $desk_ids
        channels: $channels
        channel_types: $channel_types
        agent_ids: $agent_ids
        csat_human_agent: $csat_human_agent
        min_num_user_messages: $min_num_user_messages
        is_test: $is_test
        user_rating: $user_rating
      }
    ) {
      ${EFFECTIVENESS_TIMESERIES_FIELDS}
    }
  }
`;

export const useHumanAgentsEffectivenessTimeseries = () => {
  const { isComparing } = useComparisonMode();
  const { startDate, endDate } = useSelector(selectAnalyticsDates);

  const isSingleDay = isSingleDayRange(startDate, endDate);

  const { variables, skip } = useHumanAgentsQueryVariables();
  const comparisonDates = useMemo(
    () => getComparisonDates(startDate, endDate),
    [startDate, endDate]
  );
  const { variables: comparisonVariables } = useHumanAgentsQueryVariables(comparisonDates);

  const { loading, data, error } = useQuery<QueryResponse>(GET_HUMAN_AGENTS_DURATIONS_TIMESERIES, {
    variables,
    skip: skip || isSingleDay,
  });

  const { loading: comparisonLoading, data: comparisonData } = useQuery<QueryResponse>(
    GET_HUMAN_AGENTS_DURATIONS_TIMESERIES_COMPARISON,
    { variables: comparisonVariables, skip: skip || !isComparing || isSingleDay }
  );

  return {
    isLoading: loading || (isComparing && comparisonLoading),
    error,
    data: data?.rows ?? [],
    comparisonData: comparisonData?.rows ?? [],
  };
};
